// ── 홍보물월정산 — 마곡으로 셀 마케터 명단 (2026-10-05) ─────────────────────────
// 고른 마케터가 맡은 계약은 콜을 누가 썼든 마곡 장수에 들어간다. "몇 월부터 누구" 로 저장해
// 고른 달부터만 바뀌고 그 전 달 금액은 그대로다(서버 promotional-monthly-magok.service.js).

import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/Modal';
import { apiClient } from '../../api/apiClient';
import { stdBlackBtn, stdOutlineBtn } from './adminlayout/designStandard';

const API = '/api/admin/promotional-monthly-settlement-magok-marketers';

// 'YYYY-MM' 더하기 (Date 를 쓰지 않는 글자 계산)
function addMonths(ym, diff) {
  const index = Number(ym.slice(0, 4)) * 12 + Number(ym.slice(5, 7)) - 1 + diff;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

function monthOptions(startYm, endYm) {
  const out = [];
  for (let ym = endYm, guard = 0; ym >= startYm && guard < 60; ym = addMonths(ym, -1), guard += 1) out.push(ym);
  return out;
}

// 그 달에 적용되는 명단(그 달 이하 가장 늦게 시작한 줄)
function idsForMonth(periods, ym) {
  let ids = [];
  for (const p of periods) if (p.from <= ym) ids = p.marketers.map((m) => m.id);
  return ids;
}

export default function PromotionalMonthlyMagokModal({ open, onClose, onSaved, showToast, startYm, currentYm }) {
  const [periods, setPeriods] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [from, setFrom] = useState(currentYm);
  const [checked, setChecked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const months = useMemo(() => monthOptions(startYm, addMonths(currentYm, 1)), [startYm, currentYm]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    (async () => {
      const res = await apiClient.get(API);
      if (!active) return;
      setLoading(false);
      if (!res.ok || !res.data?.success) {
        showToast?.(res.data?.message || '명단을 불러오지 못했습니다.', 'error');
        return;
      }
      const data = res.data.data || {};
      setPeriods(data.periods || []);
      setCandidates(data.candidates || []);
      setFrom(currentYm);
      setChecked(idsForMonth(data.periods || [], currentYm));
    })();
    return () => { active = false; };
  }, [open, currentYm, showToast]);

  function changeFrom(ym) {
    setFrom(ym);
    setChecked(idsForMonth(periods, ym));
  }

  function toggle(id) {
    setChecked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function save() {
    const names = candidates.filter((c) => checked.includes(c.id)).map((c) => c.name);
    const text = names.length ? names.join(', ') : '없음';
    if (!window.confirm(`${from}부터 마곡 마케터를 [${text}](으)로 저장할까요?\n${from}부터의 마곡 장수와 파인그룹 금액이 다시 계산됩니다.`)) return;
    setSaving(true);
    const res = await apiClient.put(API, { from, ids: checked });
    setSaving(false);
    if (!res.ok || !res.data?.success) {
      showToast?.(res.data?.message || '저장에 실패했습니다.', 'error');
      return;
    }
    showToast?.(res.data.message || '저장했습니다.', 'success');
    onSaved?.();
    onClose?.();
  }

  const shownPeriods = [...periods].reverse();

  return (
    <Modal
      open={open}
      title="마곡 마케터 설정"
      icon="⚙️"
      onClose={onClose}
      width="min(460px, calc(100vw - 48px))"
      footer={(
        <div style={footerRow}>
          <button type="button" onClick={onClose} style={stdOutlineBtn} disabled={saving}>취소</button>
          <button type="button" onClick={save} style={stdBlackBtn} disabled={saving || loading}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      )}
    >
      <div style={introStyle}>
        여기서 고른 마케터가 맡은 계약은 <b>콜을 누가 썼든</b> 마곡장수에 들어갑니다.
        고른 <b>달부터</b> 적용되고, 그 전 달은 지금 금액 그대로입니다.
      </div>

      <div style={sectionLabel}>지금 명단</div>
      <div style={historyBox}>
        {shownPeriods.length === 0 && <div style={mutedText}>아직 명단이 없습니다.</div>}
        {shownPeriods.map((p) => (
          <div key={p.from} style={historyRow}>
            <span style={historyMonth}>{p.from}부터</span>
            <span>{p.marketers.length ? p.marketers.map((m) => m.name).join(', ') : '없음'}</span>
          </div>
        ))}
      </div>

      <label htmlFor="magok-from" style={sectionLabel}>적용 시작 월</label>
      <select id="magok-from" value={from} onChange={(e) => changeFrom(e.target.value)} style={selectStyle} disabled={loading || saving}>
        {months.map((ym) => <option key={ym} value={ym}>{ym}</option>)}
      </select>

      <div style={sectionLabel}>마곡으로 셀 마케터</div>
      <div style={listBox}>
        {loading && <div style={mutedText}>불러오는 중...</div>}
        {!loading && candidates.length === 0 && <div style={mutedText}>고를 수 있는 마케터가 없습니다.</div>}
        {candidates.map((c) => (
          <label key={c.id} style={checkRow}>
            <input type="checkbox" checked={checked.includes(c.id)} onChange={() => toggle(c.id)} disabled={saving} />
            <span style={{ fontWeight: 700 }}>{c.name}</span>
            <span style={mutedText}>{c.companyCode}{c.isActive ? '' : ' · 퇴사'}</span>
          </label>
        ))}
      </div>
    </Modal>
  );
}

const introStyle = { fontSize: 13, color: 'var(--text-sub)', lineHeight: 1.6, marginBottom: 14 };
const sectionLabel = { display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text)', margin: '12px 0 6px' };
const historyBox = { border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', background: 'var(--bg-subtle)', fontSize: 13, color: 'var(--text)' };
const historyRow = { display: 'flex', gap: 10, padding: '3px 0' };
const historyMonth = { fontWeight: 700, minWidth: 82, fontVariantNumeric: 'tabular-nums' };
const selectStyle = { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, background: 'var(--bg-card)', color: 'var(--text)', boxSizing: 'border-box' };
const listBox = { border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px', maxHeight: 260, overflowY: 'auto' };
const checkRow = { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', fontSize: 14, color: 'var(--text)', cursor: 'pointer' };
const mutedText = { fontSize: 12, color: 'var(--text-muted)' };
const footerRow = { display: 'flex', gap: 8, justifyContent: 'flex-end' };
